import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { Observable } from 'rxjs';
import { FormBase } from '../../../_classes/form-base.class';
import IUser from '../../../_interfaces/IUser';
import IJob from '../../../_interfaces/IJob';
import IRole from '../../../_interfaces/IRole';
import { EResources } from '../../../_resources/EResources';
import { CustomersService } from '../../../_services/customers/customers.service';
import { JobsService } from '../../../_services/jobs/jobs.service';
import { RolesService } from '../../../_services/roles/roles.service';
import { SessionService } from '../../../_services/session/session.service';
import { UsersService } from '../../../_services/users/users.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';
import { AdminPasswordRulesComponent } from '../../admin-ui/admin-password-rules/admin-password-rules.component';

@Component({
  selector: 'app-form-user',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    InputTextModule,
    DatePickerModule,
    ButtonModule,
    SelectModule,
    PasswordModule,
    AdminFormActionsComponent,
    AdminPasswordRulesComponent,
  ],
  templateUrl: './form-user.component.html',
  styleUrl: './form-user.component.scss',
})
export class FormUserComponent extends FormBase<IUser> implements OnInit, OnChanges {
  @Input() showIntro = true;
  @Input() showPersonalSection = true;
  @Input() showPersonalSectionHeader = true;
  @Input() showEmployeeSection = true;
  @Input() showCompensationSection = true;
  @Input() showCreateAccessSection = true;
  @Input() showCredentialsSection = true;
  public jobs: IJob[] = [];
  public roles: IRole[] = [];
  public submitted = false;
  public submitting = false;
  public accessMode: 'manual' | 'all-clinics' = 'manual';
  public defaultRoleId: number | null = null;
  public accessErrors: { defaultRole?: string } = {};
  public costHint: { currency_code: string; effective_price: number; additional_charge: boolean } | null = null;
  public loadingCostHint = false;
  public readonly maxBirthdayDate = this.toStartOfDay(new Date());
  public readonly minBirthdayDate = new Date(
    this.maxBirthdayDate.getFullYear() - 120,
    this.maxBirthdayDate.getMonth(),
    this.maxBirthdayDate.getDate(),
  );
  public readonly maxHireDate = this.maxBirthdayDate;

  public form: ReturnType<FormBuilder['group']>;

  constructor(
    public readonly session: SessionService,
    private readonly userService: UsersService,
    private readonly validationService: ValidatorService,
    public readonly messageService: MessageService,
    public readonly translate: TranslateService,
    private readonly jobsService: JobsService,
    private readonly rolesService: RolesService,
    private readonly customersService: CustomersService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
  ) {
    super(session, messageService, translate);
    this.form = this.fb.group({
      name: [''],
      surname: [''],
      birthday: [null as Date | null],
      hire_date: [null as Date | null],
      email: ['', [Validators.email]],
      job: [null as IJob | null],
      password: [''],
      passwordRepeat: [''],
      compensationFixedEnabled: [false],
      compensationFixedAnnual: [null as number | null],
      compensationTreatmentEnabled: [false],
      compensationTreatmentPercent: [null as number | null],
      compensationSalesEnabled: [false],
      compensationSalesPercent: [null as number | null],
    });
  }

  async execute(): Promise<void> {
    this.submitted = true;
    this.clearErrors();
    this.accessErrors = {};

    this.applyDateValidationErrors();

    if (this.form.invalid || this.hasValidationErrors()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.mode === 'create' && this.accessMode === 'all-clinics' && !this.defaultRoleId) {
      this.accessErrors.defaultRole = this.translate.instant(
        'ADMIN.USERS.CREATE_ACCESS.DEFAULT_ROLE_REQUIRED',
      );
      return;
    }

    this.syncPayloadFromForm();

    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  clear(): void {
    this.payload = {
      name: '',
      surname: '',
      password: '',
      passwordRepeat: '',
      email: '',
      hire_date: undefined,
      birthday: undefined,
      job: undefined,
    };

    this.form.reset({
      name: '',
      surname: '',
      birthday: null,
      hire_date: null,
      email: '',
      job: null,
      password: '',
      passwordRepeat: '',
      compensationFixedEnabled: false,
      compensationFixedAnnual: null,
      compensationTreatmentEnabled: false,
      compensationTreatmentPercent: null,
      compensationSalesEnabled: false,
      compensationSalesPercent: null,
    });

    this.submitted = false;
    this.accessMode = 'manual';
    this.defaultRoleId = null;
    this.accessErrors = {};
  }

  private syncPayloadFromForm(): void {
    const raw = this.form.getRawValue();
    this.payload = {
      ...this.payload,
      name: raw.name || '',
      surname: raw.surname || '',
      birthday: raw.birthday || undefined,
      hire_date: raw.hire_date || undefined,
      email: raw.email || '',
      job: raw.job || undefined,
      password: raw.password || '',
      passwordRepeat: raw.passwordRepeat || '',
    };

    this.applySectionBasedExclusions();
  }

  private syncFormFromPayload(): void {
    this.form.patchValue({
      name: this.payload?.name || '',
      surname: this.payload?.surname || '',
      birthday: this.toDate(this.payload?.birthday),
      hire_date: this.toDate(this.payload?.hire_date),
      email: this.payload?.email || '',
      job: this.payload?.job || null,
      password: this.payload?.password || '',
      passwordRepeat: this.payload?.passwordRepeat || '',
    });
  }

  get formTitleKey(): string {
    return this.mode === 'edit'
      ? 'ADMIN.USERS.DETAIL.PROFILE.FORM_TITLE'
      : 'ADMIN.USERS.NEW';
  }

  get formDescriptionKey(): string {
    return this.mode === 'edit'
      ? 'ADMIN.USERS.DETAIL.PROFILE.FORM_DESCRIPTION'
      : 'ADMIN.IA.USERS.DESCRIPTION';
  }

  generatePayload(): IUser {
    const payload: IUser = {} as IUser;

    this.properties().forEach((key: string) => {
      if (!this.exclude.includes(key) && this.payload[key]) {
        payload[key] = this.payload[key];
      }
    });

    return payload;
  }

  getResourceName(): string {
    return EResources.USERS;
  }

  updateFn(payload: IUser): Observable<any> {
    return this.userService.update(this.customers_id, this.id, payload);
  }

  createFn(payload: IUser): Observable<any> {
    if (this.mode === 'create') {
      return this.userService.create(this.customers_id, {
        email: payload.email,
        display_name_override: payload.name || null,
        display_surname_override: payload.surname || null,
        access_mode: this.accessMode,
        default_role_id: this.accessMode === 'all-clinics' ? this.defaultRoleId : null,
      } as any);
    }
    return this.userService.create(this.customers_id, payload);
  }

  properties(): string[] {
    return [
      'name',
      'surname',
      'email',
      'password',
      'birthday',
      'hire_date',
      'job',
    ];
  }

  validate(): Promise<void> {
    if (this.mode === 'create') {
      const errors = {} as any;
      const email = `${this.payload?.email || ''}`.trim();
      if (!email) {
        errors.email = 'ERRORS.USER.EMAIL';
      } else if (this.form.controls['email'].errors) {
        errors.email = 'ERRORS.EMAIL.INVALID';
      }

      if (Object.keys(errors).length) {
        return Promise.reject(errors);
      }

      return Promise.resolve();
    }
    return this.validationService.user(this.payload, this.exclude);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.applySectionBasedExclusions();
    this.loadCostHint();
    this.jobsService.getJobs(this.customers_id).subscribe((jobs) => {
      this.jobs = jobs;
    });
    this.rolesService.getByCustomer(this.customers_id).subscribe((roles) => {
      this.roles = roles || [];
    });
    this.syncFormFromPayload();
  }

  private loadCostHint(): void {
    if (this.mode !== 'create' || !this.customers_id) {
      this.costHint = null;
      return;
    }
    this.loadingCostHint = true;
    this.customersService.getCustomerCostHints(this.customers_id).subscribe({
      next: (hints) => {
        this.costHint = {
          currency_code: `${hints?.currency_code || 'EUR'}`,
          effective_price: Number(hints?.staff_user?.effective_price || 0),
          additional_charge: Number(hints?.staff_user?.effective_price || 0) > 0,
        };
        this.loadingCostHint = false;
      },
      error: () => {
        this.costHint = null;
        this.loadingCostHint = false;
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['payload']) {
      return;
    }

    const currentPayload = changes['payload'].currentValue as IUser | null | undefined;
    if (!currentPayload) {
      return;
    }

    this.payload = currentPayload as IUser;
    if (this.mode !== 'edit') {
      this.mode = 'edit';
    }
    this.id = +(currentPayload?.id || this.id || 0) || this.id;
    this.syncFormFromPayload();
  }

  override create(payload: IUser): void {
    this.createFn(payload)
      .subscribe({
        next: (response: any) => {
          this.clear();
          if (this.mode === 'create') {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('COMMON.SAVE'),
              detail: this.translate.instant('ADMIN.USERS.INVITATION.SUCCESS', {
                email: response?.email || payload.email,
              }),
            });
          } else {
            this.createMessageSuccess();
          }
          this.onCreate();
          this.redirectToCreatedUser(this.resolveCreatedUserId(response));
        },
        error: (error: any) => {
          const code = `${error?.error?.message || ''}`.trim();
          if (code === 'SUBSCRIPTION_STAFF_LIMIT_REACHED') {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('COMMON.ERROR'),
              detail: this.translate.instant('ADMIN.USERS.INVITATION.LIMIT_REACHED'),
            });
            return;
          }
          this.createMessageError();
        },
      });
  }

  private resolveCreatedUserId(createdUser: unknown): number {
    if (!createdUser || typeof createdUser !== 'object') {
      return 0;
    }

    // En el modo actual de onboarding, el backend devuelve una invitación
    // (id de invitación), no un usuario creado. Solo redirigimos si llega users_id real.
    const id = Number((createdUser as any).users_id || (createdUser as any).user?.id || 0);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  private applySectionBasedExclusions(): void {
    const nextExclude = new Set([...(this.exclude || [])]);

    if (!this.showPersonalSection) {
      nextExclude.add('email');
      nextExclude.add('birthday');
    }

    if (!this.showEmployeeSection || this.mode === 'create') {
      nextExclude.add('job');
      nextExclude.add('hire_date');
    }

    if (!this.showCredentialsSection || this.mode === 'create') {
      nextExclude.add('password');
      nextExclude.add('passwordRepeat');
    }

    this.exclude = Array.from(nextExclude);
  }

  private redirectToCreatedUser(createdUserId: number): void {
    if (!createdUserId || !this.router.url.includes('/administration')) {
      return;
    }

    const customerParam = this.customer_route_param || `${this.customers_id}`;
    this.router.navigate([
      '/administration',
      customerParam,
      'users',
      createdUserId,
      'profile',
    ]);
  }

  public hasFieldError(field: keyof IUser | string): boolean {
    if (this.errors?.[field]) {
      return true;
    }

    const control = (this.form.controls as Record<string, any>)[`${field}`];
    return !!control && (this.submitted || control.touched) && !!control.errors;
  }

  public getFieldError(field: keyof IUser | string): string {
    if (this.errors?.[field]) {
      return this.errors[field];
    }

    const control = (this.form.controls as Record<string, any>)[`${field}`];
    if (!control || !(this.submitted || control.touched) || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'ERRORS.USER.NAME';
    }
    if (control.errors['email']) {
      return 'ERRORS.EMAIL.INVALID';
    }

    return '';
  }

  public get compensationSummary(): string[] {
    const summary: string[] = [];
    const raw = this.form.getRawValue();

    if (raw.compensationFixedEnabled && raw.compensationFixedAnnual) {
      summary.push(
        this.translate.instant('ADMIN.USERS.COMPENSATION.SUMMARY.FIXED', {
          amount: raw.compensationFixedAnnual,
        }),
      );
    }

    if (raw.compensationTreatmentEnabled && raw.compensationTreatmentPercent !== null) {
      summary.push(
        this.translate.instant('ADMIN.USERS.COMPENSATION.SUMMARY.TREATMENTS', {
          value: raw.compensationTreatmentPercent,
        }),
      );
    }

    if (raw.compensationSalesEnabled && raw.compensationSalesPercent !== null) {
      summary.push(
        this.translate.instant('ADMIN.USERS.COMPENSATION.SUMMARY.SALES', {
          value: raw.compensationSalesPercent,
        }),
      );
    }

    return summary;
  }

  private hasValidationErrors(): boolean {
    return Object.values(this.errors || {}).some((value) => !!value);
  }

  private applyDateValidationErrors(): void {
    if (this.mode === 'create') {
      this.errors.birthday = undefined;
      this.errors.hire_date = undefined;
      return;
    }
    const birthday = this.toDate(this.form.controls['birthday'].value);
    const hireDate = this.toDate(this.form.controls['hire_date'].value);
    const today = this.maxBirthdayDate;

    if (birthday) {
      if (birthday > today) {
        this.errors.birthday = 'ERRORS.USER.BIRTHDAY_FUTURE';
      } else if (birthday < this.minBirthdayDate) {
        this.errors.birthday = 'ERRORS.USER.BIRTHDAY_TOO_OLD';
      } else {
        this.errors.birthday = undefined;
      }
    } else {
      this.errors.birthday = undefined;
    }

    if (hireDate) {
      if (hireDate > this.maxHireDate) {
        this.errors.hire_date = 'ERRORS.USER.HIRE_DATE_FUTURE';
      } else if (birthday && hireDate < birthday) {
        this.errors.hire_date = 'ERRORS.USER.HIRE_DATE_BEFORE_BIRTHDAY';
      } else {
        this.errors.hire_date = undefined;
      }
    } else {
      this.errors.hire_date = undefined;
    }
  }

  private toDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.valueOf()) ? null : this.toStartOfDay(value);
    }
    const parsed = new Date(value as string);
    return Number.isNaN(parsed.valueOf()) ? null : this.toStartOfDay(parsed);
  }

  private toStartOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

}
